import { useRef, useState } from 'react';
import { useRouter } from 'next/router';
import ReCAPTCHA from 'react-google-recaptcha';
import { z } from 'zod';

import { Box } from '@chakra-ui/react';

import { Alert, AlertMessage } from '~/components/alert';
import { LoadingOverlay } from '~/components/loading-overlay';
import { TemplateForm } from '~/components/template-form';
import { useLeadFormEvent } from '~/features/analytics/hooks/use-lead-form-event';
import { postPersonalizedDiscountFlatForm } from '~/features/discount/controllers/post-personalized-discount-flat-form';
import { addCouponCookie } from '~/features/discount/utils/add-coupon-cookie';
import { publicEnv } from '~/features/environment/public';
import { TemplateForm as TemplateFormType } from '~/features/page-template/models/template-form';
import { postSignup } from '~/features/quick-authentication/controllers/post-signup';
import { QuickAuthenticationError } from '~/features/quick-authentication/errors';
import { useUser } from '~/features/user/hooks/use-user';
import { transformFormDataToEventProperties } from '~/utils/transform-form-data-to-event-properties';

const formFieldDefinitions = [
  {
    id: '1',
    order: 0,
    required: true,
    label: 'Email address',
    inputType: 'email',
    placeholder: 'you@example.com',
    name: 'email',
  },
] as const;

const formTemplate: TemplateFormType = {
  _id: 'new-user-offer-form',
  _type: 'form',
  formName: 'New User Offer',
  formData: JSON.stringify(formFieldDefinitions),
  submitButtonText: 'Get my offer',
  cancelButtonText: 'Cancel',
  submitButton: null,
  cancelButton: null,
  sendToSalesforce: false,
  legalText:
    'By submitting this form, you agree to our [Terms of Use](https://www.udacity.com/legal/terms-of-use) and [Privacy Policy](https://www.udacity.com/legal/privacy).',
} as const;

const newUserOfferFormSchema = z.object({
  email: z.string().email(),
});

export function NewUserOfferForm() {
  const router = useRouter();
  const { user, userId } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState<AlertMessage | undefined>();
  const [isCaptchaRequired, setIsCaptchaRequired] = useState(false);
  const [captchaValue, setCaptchaValue] = useState<string>();
  const hasSignedUpRef = useRef(false);
  const { isSubmitting, sendSegmentLeadFormEvents } = useLeadFormEvent();

  async function handleNewUserOfferForm({
    formData,
  }: {
    templateForm: TemplateFormType;
    formData: Record<string, string>;
  }) {
    setIsLoading(true);
    setFormError(undefined);
    setIsCaptchaRequired(false);

    const parsed = newUserOfferFormSchema.parse(formData);

    if (!userId && !hasSignedUpRef.current) {
      try {
        await postSignup(parsed.email, captchaValue);
        hasSignedUpRef.current = true;
      } catch (error) {
        if (error instanceof QuickAuthenticationError) {
          switch (error.name) {
            case 'EMAIL_EXISTS':
              window.location.href = `${publicEnv.NEXT_PUBLIC_AUTH_URL}/sign-in?next=${window.location.href}`;
              setFormError({
                message: 'An account with this email already exists, please login to continue.',
                level: 'error',
              });
              break;
            case 'CAPTCHA_REQUIRED':
              setFormError({ message: error.message, level: 'warning' });
              setIsCaptchaRequired(true);
              break;
            default:
              setFormError({ message: 'An error occurred signing up. Please try again.', level: 'error' });
          }
        } else {
          setFormError({ message: 'An error occurred signing up. Please try again.', level: 'error' });
        }
        setIsLoading(false);
        return;
      }
    }

    try {
      const couponResponse = await postPersonalizedDiscountFlatForm(parsed);

      addCouponCookie(couponResponse.couponCode, {
        expires: new Date(couponResponse.expiresAt),
      });

      const params = new URLSearchParams({
        couponCode: encodeURIComponent(couponResponse.couponCode),
        percentDiscount: encodeURIComponent(couponResponse.percentDiscount),
        expiresAt: encodeURIComponent(couponResponse.expiresAt),
      }).toString();

      // Don't block the offer redirect on analytics. Segment can be blocked
      // by privacy tooling and leave its promise pending indefinitely.
      void sendSegmentLeadFormEvents({
        identifyTraits: {
          email: parsed.email,
        },
        trackAnalyticsEvent: {
          name: 'Marketing Qualified Lead Form Submitted',
          type: 'submit',
          label: 'Personalized Discount',
          action: 'form_submitted',
          additionalAttributes: transformFormDataToEventProperties(formData),
        },
        integrations: {
          sendToSalesforce: true,
        },
      });

      await router.push(`/personalized-discount-success?${params}`);
    } catch (error) {
      setFormError({ message: 'An error occurred creating your offer. Please try again.', level: 'error' });
      setIsLoading(false);
    }
  }

  return (
    <>
      {formError && <Alert status={formError.level}>{formError.message}</Alert>}

      {isCaptchaRequired && (
        <Box marginTop='16px'>
          <ReCAPTCHA
            sitekey={publicEnv.NEXT_PUBLIC_USER_API_RECAPTCHA_KEY}
            size='normal'
            onChange={(value) => {
              if (value) {
                setCaptchaValue(value);
              }
            }}
          />
        </Box>
      )}

      <Box marginTop={formError || isCaptchaRequired ? '24px' : undefined} position='relative'>
        {isLoading && <LoadingOverlay message='Creating your offer...' />}
        <TemplateForm
          key={user?.email}
          defaultValues={{ email: user?.email || '' }}
          isFullWidth={true}
          templateForm={formTemplate}
          inputStyleProps={{
            _placeholder: { color: 'grey.400' },
            borderColor: 'grey.500',
          }}
          submitButtonProps={{
            isLoading: isSubmitting,
            bg: 'blue.500',
            borderRadius: '4px',
            color: 'white',
            fontSize: '15px',
            fontWeight: 500,
            padding: '14px 24px',
            _hover: { bg: 'blue.700' },
          }}
          onCancelClick={router.back}
          onSubmit={handleNewUserOfferForm}
        />
      </Box>
    </>
  );
}
